import { useState } from 'react';
import { NJTabs, NJTab, NJTabPanel, NJText } from '@engie-group/fluid-design-system-react';
import { UM } from '../_components/tokens';
import { USAGE_STAGES } from './mocks';
import { sortEngieFirst } from './hooks';
import { LayerRow } from './LayerRow';
import type { LayerItem, UsageStage } from './types';

/**
 * Proposition A — Navigation par onglets "Étapes d'étude".
 *
 * Colle au parcours métier séquentiel décrit dans le recueil de besoins :
 * l'utilisateur avance étape par étape (conso → ressources → tracé → futur →
 * annexes) et ne voit qu'une catégorie à la fois, ce qui limite la charge
 * cognitive. Un onglet "Favoris" est épinglé en premier.
 */
export function ProposalATabs({
  favoriteLayers,
  layers,
  toggleVisibility,
  toggleFavorite,
}: {
  favoriteLayers: LayerItem[];
  layers: LayerItem[];
  toggleVisibility: (id: string) => void;
  toggleFavorite: (id: string) => void;
}) {
  const [activeTab, setActiveTab] = useState<'favorites' | UsageStage>('favorites');

  const activeLayers =
    activeTab === 'favorites'
      ? favoriteLayers
      : sortEngieFirst(layers.filter((l) => l.stages.includes(activeTab)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto', flexShrink: 0 }}>
        <NJTabs
          label="Étapes d'étude"
          activeTab={activeTab}
          onClickTabItem={(id) => setActiveTab(id as 'favorites' | UsageStage)}
          density="compact"
        >
          <NJTab
            id="favorites"
            label={`★ Favoris (${favoriteLayers.length})`}
            ariaControls="panel-favorites"
          />
          {USAGE_STAGES.map((stage) => (
            <NJTab
              key={stage.id}
              id={stage.id}
              label={stage.label}
              ariaControls={`panel-${stage.id}`}
            />
          ))}
        </NJTabs>
      </div>

      <div style={{ flex: 1, overflow: 'hidden' }}>
      <NJTabPanel id="panel-active" isActive>
        <div style={{ height: '100%', overflowY: 'auto', padding: '12px 4px' }}>
          {activeTab !== 'favorites' && (
            <NJText scale="sm" style={{ color: UM.textMuted, marginBottom: 8, display: 'block' }}>
              {USAGE_STAGES.find((s) => s.id === activeTab)?.description}
            </NJText>
          )}

          {activeLayers.length === 0 && (
            <NJText scale="sm" style={{ color: UM.textDim }}>
              {activeTab === 'favorites'
                ? 'Aucune couche favorite pour le moment. Cliquez sur ★ dans une autre étape pour en ajouter.'
                : 'Aucune couche dans cette étape.'}
            </NJText>
          )}

          {activeLayers.map((layer) => (
            <LayerRow
              key={layer.id}
              layer={layer}
              onToggleVisibility={toggleVisibility}
              onToggleFavorite={toggleFavorite}
            />
          ))}
        </div>
      </NJTabPanel>
      </div>
    </div>
  );
}
